import React from "react";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Github, Linkedin, Mail, ExternalLink } from "lucide-react";
import myPic from "assets/images/my_pic.webp";
import { scrollToSection } from "lib/scroll";
import { useLanguage } from "contexts/LanguageContext";

const Hero: React.FC = () => {
  const { t } = useLanguage();
  const handleSectionClick = (selector: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    scrollToSection(selector);
  };

  return (
    <section className="min-h-screen flex items-center justify-center pt-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6 text-center lg:text-left">
            <div className="space-y-2">
              <Badge variant="secondary" className="text-sm px-3 py-1">
                {t.hero.badge}
              </Badge>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight">
                {t.hero.greeting} <span className="text-primary">Zakaria Dani</span>
              </h1>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-muted-foreground">
                {t.hero.subtitle}
              </h2>
            </div>

            <p className="text-lg text-muted-foreground max-w-2xl">
              {t.hero.intro}{" "}
              <span className="text-foreground font-semibold">{t.hero.secure}</span>,{" "}
              <span className="text-foreground font-semibold">{t.hero.scalable}</span>{" "}
              {t.hero.and}{" "}
              <span className="text-foreground font-semibold">
                {t.hero.userFriendly}
              </span>{" "}
              {t.hero.outro}
            </p>

            {/* Key Skills */}
            <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
              {[
                "Java",
                "React",
                "Spring Boot",
                "TypeScript",
                "PostgreSQL",
                "Docker",
                "Microservices",
              ].map((skill) => (
                <Badge key={skill} variant="outline" className="text-sm">
                  {skill}
                </Badge>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
              <Button size="lg" asChild>
                <a href="#contact" onClick={handleSectionClick("#contact")}>
                  <Mail className="mr-2 h-4 w-4" />
                  {t.hero.getInTouch}
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="#projects" onClick={handleSectionClick("#projects")}>
                  {t.hero.viewProjects}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>

            {/* Social Links */}
            <div className="flex gap-4 justify-center lg:justify-start pt-4">
              <a
                href="https://github.com/ZakariaDani"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                <Github className="h-6 w-6" />
                <span className="sr-only">GitHub</span>
              </a>
              <a
                href="https://www.linkedin.com/in/zakaria-dani-5a2961121/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                <Linkedin className="h-6 w-6" />
                <span className="sr-only">LinkedIn</span>
              </a>
              <a
                href="mailto:zakariadani25@gmail.com"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                <Mail className="h-6 w-6" />
                <span className="sr-only">Email</span>
              </a>
            </div>
          </div>

          {/* Right Content - Image/Visual */}
          <div className="relative flex items-center justify-center">
            <div className="relative w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96">
              {/* Animated gradient background */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-accent/20 rounded-full blur-3xl animate-pulse"></div>

              {/* Profile Image Container */}
              <div className="relative w-full h-full rounded-full border-4 border-primary/20 overflow-hidden bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
                <img
                  src={myPic}
                  alt="Zakaria Dani"
                  className="w-full h-full object-cover rounded-full scale-100"
                  style={{ objectPosition: "center" }}
                />
              </div>

              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-background border-2 border-primary rounded-full p-3 shadow-lg">
                <svg
                  className="w-8 h-8 text-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                  />
                </svg>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-background border-2 border-accent rounded-full p-3 shadow-lg">
                <svg
                  className="w-8 h-8 text-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
